import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ChevronDown, ChevronRight, AlertCircle, Plus, Pencil, Check, X } from 'lucide-react';
import { format } from 'date-fns';
import { ArtifactList } from '@/components/artifacts/ArtifactList';
import { ArtifactUploader } from '@/components/artifacts/ArtifactUploader';
import { ArtifactLinkCapture } from '@/components/artifacts/ArtifactLinkCapture';
import { LINK_ENTITY_TYPES, LINK_ROLES } from '@/lib/artifacts/constants';
import type { WorkflowComplaint } from '@/hooks/useWorkflowReport';

interface WorkflowComplaintsSectionProps {
  complaints: WorkflowComplaint[];
  canEdit: boolean;
  onAdd: (text: string) => void;
  onUpdate: (id: string, text: string) => void;
  shopId?: string;
}

export function WorkflowComplaintsSection({ complaints, canEdit, onAdd, onUpdate, shopId }: WorkflowComplaintsSectionProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const handleAdd = () => {
    if (!newText.trim()) return;
    onAdd(newText.trim());
    setNewText('');
    setIsAdding(false);
  };

  const handleUpdate = () => {
    if (!editingId || !editText.trim()) return;
    onUpdate(editingId, editText.trim());
    setEditingId(null);
    setEditText('');
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 rounded-lg border">
          <div className="flex items-center gap-2">
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <AlertCircle className="h-4 w-4 text-orange-500" />
            <span className="font-semibold text-sm">Customer Complaints</span>
            <Badge variant="secondary" className="text-xs">{complaints.length}</Badge>
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pt-2 pb-4 space-y-3">
          {complaints.map(c => (
            <div key={c.id} className="bg-muted/30 rounded-lg p-3 space-y-1">
              {editingId === c.id ? (
                <div className="space-y-2">
                  <Textarea value={editText} onChange={e => setEditText(e.target.value)} rows={2} />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleUpdate}><Check className="h-3 w-3 mr-1" />Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}><X className="h-3 w-3 mr-1" />Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm whitespace-pre-wrap">{c.text}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{c.author_name || 'System'} • {format(new Date(c.created_at), 'MMM d, h:mm a')}</span>
                    {canEdit && (
                      <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => { setEditingId(c.id); setEditText(c.text); }}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  {/* Artifacts */}
                  {shopId && (
                    <>
                      <ArtifactList
                        shopId={shopId}
                        entityType={LINK_ENTITY_TYPES.WORKFLOW_COMPLAINT}
                        entityId={c.id}
                        canEdit={canEdit}
                        className="mt-2"
                      />
                      {canEdit && (
                        <div className="flex items-center gap-1 mt-1">
                          <ArtifactUploader
                            shopId={shopId}
                            entityType={LINK_ENTITY_TYPES.WORKFLOW_COMPLAINT}
                            entityId={c.id}
                            role={LINK_ROLES.COMPLAINT_SUPPORT}
                          />
                          <ArtifactLinkCapture
                            shopId={shopId}
                            entityType={LINK_ENTITY_TYPES.WORKFLOW_COMPLAINT}
                            entityId={c.id}
                            role={LINK_ROLES.COMPLAINT_SUPPORT}
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          ))}

          {isAdding ? (
            <div className="space-y-2">
              <Textarea value={newText} onChange={e => setNewText(e.target.value)} placeholder="Describe the customer complaint..." rows={2} autoFocus />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAdd}><Check className="h-3 w-3 mr-1" />Add</Button>
                <Button size="sm" variant="ghost" onClick={() => { setIsAdding(false); setNewText(''); }}><X className="h-3 w-3 mr-1" />Cancel</Button>
              </div>
            </div>
          ) : canEdit && (
            <Button size="sm" variant="outline" onClick={() => setIsAdding(true)}>
              <Plus className="h-3 w-3 mr-1" />Add Complaint
            </Button>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
